import React, { useId, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Input, Label, FormGroup } from 'reactstrap';

import './checkbox-item.scss';

export const CheckboxItem = memo(({ label, checked, onChange, ...rest }) => {
  const id = useId();
  const inputRef = React.useRef();

  /**
   * Focus handler for checkbox label clicks to prevent FocusScope interference.
   *
   * Problem: When a scrollable checkbox list is inside a modal with focus containment
   * (React Aria's FocusScope with contain=true), clicking on checkbox labels can trigger
   * incorrect focus restoration behavior. This occurs because:
   *
   * 1. Label clicks generate blur events with e.relatedTarget === null
   * 2. FocusScope's blur handler uses requestAnimationFrame + document.activeElement
   *    to determine if focus moved outside the scope
   * 3. When relatedTarget is null, the timing becomes unreliable - activeElement
   *    may still reference the old element or document.body
   * 4. FocusScope incorrectly assumes focus left the scope and restores focus
   *    to the previously focused element, causing unwanted scrolling
   *
   * Root cause: Browser inconsistency in setting relatedTarget for label->input
   * focus transfers, especially in scrollable containers.
   *
   * Solution: Intercept pointer events on labels and manually handle checkbox focus.
   * This bypasses the problematic browser focus sequence entirely while
   * maintaining the same user experience. The manual focus() call happens
   * synchronously, preventing FocusScope's async logic from interfering.
   *
   * URL: https://github.com/adobe/react-spectrum/issues/1121
   */
  const labelHandler = useCallback(() => {
    inputRef.current.focus();
  }, []);

  return (
    <FormGroup tag="li" className="checkbox-wrapper" noMargin>
      <Input
        id={id}
        type="checkbox"
        className="visually-hidden"
        checked={checked}
        onChange={onChange}
        innerRef={inputRef}
        {...rest}
      />
      <Label
        htmlFor={id}
        className="d-flex align-items-center justify-content-between gap-2 rounded-8 m-0 py-0_5 mb-0_25 px-0_5"
        onPointerDown={labelHandler}
      >
        <span>{label}</span>
        <span
          className={classnames('checkbox-icon size-20', {
            'icon-checkbox-checked3 text-blue-30': checked,
            'icon-checkbox-unchecked3 text-cool-gray-50': !checked,
          })}
          aria-hidden
        />
      </Label>
    </FormGroup>
  );
});

CheckboxItem.displayName = 'CheckboxItem';

CheckboxItem.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};
